import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    LayoutDashboard, 
    FileText, 
    Package, 
    Settings, 
    LogOut,
    ChefHat,
    FolderOpen,
    X
} from 'lucide-react';

const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/quotes/new', icon: FileText, label: 'Nouveau devis' },
    { path: '/documents', icon: FolderOpen, label: 'Documents' },
    { path: '/catalogue', icon: Package, label: 'Catalogue' },
    { path: '/settings', icon: Settings, label: 'Paramètres' },
];

export default function Sidebar({ isOpen, onClose }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
        onClose?.();
    };

    const handleNavClick = () => {
        // Close sidebar on mobile after navigation
        onClose?.();
    };

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}
            
            {/* Sidebar */}
            <aside 
                className={`
                    sidebar fixed left-0 top-0 h-screen flex flex-col z-50
                    w-64 lg:w-56 xl:w-64
                    transform transition-transform duration-300 ease-in-out
                    lg:translate-x-0
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
                data-testid="sidebar"
            >
                {/* Header with close button on mobile */}
                <div className="p-4 lg:p-5 border-b border-white/5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 lg:gap-3">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg bg-[#d4af37]/10 flex items-center justify-center">
                                <ChefHat className="w-5 h-5 lg:w-6 lg:h-6 text-[#d4af37]" />
                            </div>
                            <div>
                                <h1 className="text-lg lg:text-xl font-bold text-[#f8fafc]">Traiteur Hub</h1>
                                <p className="text-[#64748b] text-xs hidden sm:block">Gestion commerciale</p>
                            </div>
                        </div>
                        {/* Close button - mobile only */}
                        <button 
                            onClick={onClose}
                            className="p-2 text-[#94a3b8] hover:text-white lg:hidden"
                            data-testid="sidebar-close-btn"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === '/'}
                            onClick={handleNavClick}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg transition-colors ${
                                    isActive
                                        ? 'bg-[#d4af37]/10 text-[#d4af37] border-l-2 border-[#d4af37]'
                                        : 'text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b]/50'
                                }`
                            }
                            data-testid={`nav-${item.path.replace(/\//g, '-') || 'home'}`}
                        >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium text-sm lg:text-base">{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* User Section */}
                <div className="p-3 lg:p-4 border-t border-white/5">
                    <div className="flex items-center gap-2 lg:gap-3 px-2 lg:px-4 py-2 lg:py-3">
                        <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-[#1e293b] flex items-center justify-center flex-shrink-0">
                            <span className="text-[#d4af37] font-semibold text-xs lg:text-sm">
                                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[#f8fafc] font-medium truncate text-xs lg:text-sm">{user?.name}</p>
                            <p className="text-[#64748b] text-xs truncate hidden sm:block">{user?.email}</p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-2 text-[#94a3b8] hover:text-red-400 transition-colors flex-shrink-0"
                            title="Déconnexion"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 lg:w-5 lg:h-5" />
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
}
